/**
 * Geometría del visor IFC en LOTES fusionados (sin three ni web-ifc: se prueba en `visorLotes.test.ts`).
 *
 * Antes cada pieza era un Mesh con su material y sus aristas como otro objeto: un modelo de 20 mil piezas
 * eran 40 mil objetos y 40 mil llamadas de dibujo por cuadro, y girarlo iba a 8 cuadros por segundo. Aquí
 * las piezas se juntan en pocos buffers grandes (un lote = posiciones, normales, índices y aristas de
 * muchas piezas) y se guarda, por pieza, qué rango del lote es suyo (un "trozo"). La selección sigue
 * siendo por pieza: el color de una pieza es su rango del atributo de color, y ocultarla es sacar su
 * rango del índice.
 *
 * Todo en coordenadas del MUNDO (la colocación de web-ifc ya aplicada), en metros.
 */

/** Campos de cada trozo en `Lote.trozos` (enteros). */
export const T_EID = 0;
export const T_V0 = 1; // primer vértice del trozo en el lote
export const T_VN = 2; // vértices
export const T_I0 = 3; // primer índice (en el índice completo del lote)
export const T_IN = 4; // índices (3 por triángulo)
export const T_A0 = 5; // primer vértice de aristas
export const T_AN = 6; // vértices de aristas (2 por arista)
export const T_PASO = 7;

export interface Lote {
  /** x,y,z por vértice (mundo, m). */
  pos: Float32Array;
  /** Normal por vértice (mundo, normalizada). */
  nor: Float32Array;
  /** Índice COMPLETO del lote (todas sus piezas). El visor arma, a partir de este, el de lo que se ve. */
  idx: Uint32Array;
  /** Aristas: pares de puntos (x,y,z,x,y,z) de todas las piezas. */
  aristas: Float32Array;
  /** T_PASO enteros por trozo (ver T_*). Una pieza puede tener varios trozos (marco + vidrio). */
  trozos: Int32Array;
  /** Color base r,g,b,a (0–1) de cada trozo: el del modelo o el de respaldo. */
  colores: Float32Array;
  /** Caja de cada trozo: minX,minY,minZ,maxX,maxY,maxZ. */
  cajas: Float32Array;
}

/**
 * Aristas "de dibujo" de una malla: donde dos caras vecinas quiebran más de `umbralGrados`, y los bordes
 * sueltos. Mismo criterio que EdgesGeometry de three.js (vértices iguales si coinciden a 4 decimales),
 * hecho aquí para calcularlo fuera del hilo de la pantalla.
 */
export function aristasDeMalla(pos: ArrayLike<number>, idx: ArrayLike<number>, umbralGrados: number): number[] {
  const out: number[] = [];
  const cosUmbral = Math.cos((umbralGrados * Math.PI) / 180);
  const clave = (i: number) =>
    `${Math.round(pos[i * 3]! * 1e4)},${Math.round(pos[i * 3 + 1]! * 1e4)},${Math.round(pos[i * 3 + 2]! * 1e4)}`;
  const bordes = new Map<string, { a: number; b: number; nx: number; ny: number; nz: number } | null>();
  for (let t = 0; t + 2 < idx.length; t += 3) {
    const v = [idx[t]!, idx[t + 1]!, idx[t + 2]!];
    const h = v.map(clave);
    if (h[0] === h[1] || h[1] === h[2] || h[0] === h[2]) continue; // triángulo degenerado
    const [a, b, c] = v as [number, number, number];
    const ux = pos[b * 3]! - pos[a * 3]!, uy = pos[b * 3 + 1]! - pos[a * 3 + 1]!, uz = pos[b * 3 + 2]! - pos[a * 3 + 2]!;
    const wx = pos[c * 3]! - pos[a * 3]!, wy = pos[c * 3 + 1]! - pos[a * 3 + 1]!, wz = pos[c * 3 + 2]! - pos[a * 3 + 2]!;
    let nx = uy * wz - uz * wy, ny = uz * wx - ux * wz, nz = ux * wy - uy * wx;
    const L = Math.hypot(nx, ny, nz) || 1;
    nx /= L; ny /= L; nz /= L;
    for (let j = 0; j < 3; j++) {
      const k = (j + 1) % 3;
      const directa = `${h[j]}_${h[k]}`;
      const inversa = `${h[k]}_${h[j]}`;
      const otra = bordes.get(inversa);
      if (otra) {
        if (nx * otra.nx + ny * otra.ny + nz * otra.nz <= cosUmbral) {
          out.push(pos[otra.a * 3]!, pos[otra.a * 3 + 1]!, pos[otra.a * 3 + 2]!, pos[otra.b * 3]!, pos[otra.b * 3 + 1]!, pos[otra.b * 3 + 2]!);
        }
        bordes.set(inversa, null);
      } else if (!bordes.has(directa)) {
        bordes.set(directa, { a: v[j]!, b: v[k]!, nx, ny, nz });
      }
    }
  }
  for (const e of bordes.values()) {
    if (!e) continue;
    out.push(pos[e.a * 3]!, pos[e.a * 3 + 1]!, pos[e.a * 3 + 2]!, pos[e.b * 3]!, pos[e.b * 3 + 1]!, pos[e.b * 3 + 2]!);
  }
  return out;
}

/** Aplica una matriz 4×4 en orden de columnas (la `flatTransformation` de web-ifc) a posiciones y normales. */
export function colocar(
  pos: ArrayLike<number>,
  nor: ArrayLike<number> | null,
  t: ArrayLike<number>,
): { pos: Float32Array; nor: Float32Array } {
  const n = pos.length;
  const P = new Float32Array(n);
  const N = new Float32Array(n);
  for (let k = 0; k < n; k += 3) {
    const x = pos[k]!, y = pos[k + 1]!, z = pos[k + 2]!;
    P[k] = t[0]! * x + t[4]! * y + t[8]! * z + t[12]!;
    P[k + 1] = t[1]! * x + t[5]! * y + t[9]! * z + t[13]!;
    P[k + 2] = t[2]! * x + t[6]! * y + t[10]! * z + t[14]!;
    if (nor) {
      const a = nor[k]!, b = nor[k + 1]!, c = nor[k + 2]!;
      // La colocación de web-ifc es rotación + escala uniforme: basta rotar y normalizar.
      let X = t[0]! * a + t[4]! * b + t[8]! * c;
      let Y = t[1]! * a + t[5]! * b + t[9]! * c;
      let Z = t[2]! * a + t[6]! * b + t[10]! * c;
      const L = Math.hypot(X, Y, Z) || 1;
      X /= L; Y /= L; Z /= L;
      N[k] = X; N[k + 1] = Y; N[k + 2] = Z;
    }
  }
  return { pos: P, nor: N };
}

/**
 * Junta trozos en lotes. `limiteVertices` es un tope TÉCNICO del tamaño de cada buffer (para poder mandar
 * el modelo por partes y que la pantalla muestre avance mientras llega), no un umbral de obra.
 */
export class ArmadorDeLotes {
  private pos: Float32Array[] = [];
  private nor: Float32Array[] = [];
  private idx: Uint32Array[] = [];
  private ari: number[][] = [];
  private trozos: number[] = [];
  private colores: number[] = [];
  private cajas: number[] = [];
  private nV = 0;
  private nI = 0;
  private nA = 0;

  constructor(private readonly limiteVertices: number) {}

  get vertices(): number {
    return this.nV;
  }

  /** Agrega el trozo de una pieza (ya colocado en el mundo). Devuelve un lote si este se llenó. */
  agregar(eid: number, pos: Float32Array, nor: Float32Array, idx: ArrayLike<number>, rgba: readonly number[], aristas: number[]): Lote | null {
    const vn = pos.length / 3;
    if (vn === 0 || idx.length === 0) return null; // pieza sin malla: nada que dibujar ni encuadrar
    const base = this.nV;
    const I = new Uint32Array(idx.length);
    for (let i = 0; i < idx.length; i++) I[i] = idx[i]! + base;
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let k = 0; k < pos.length; k += 3) {
      const x = pos[k]!, y = pos[k + 1]!, z = pos[k + 2]!;
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (y < y0) y0 = y; if (y > y1) y1 = y;
      if (z < z0) z0 = z; if (z > z1) z1 = z;
    }
    this.trozos.push(eid, base, vn, this.nI, I.length, this.nA, aristas.length / 3);
    this.colores.push(rgba[0]!, rgba[1]!, rgba[2]!, rgba[3]!);
    this.cajas.push(x0, y0, z0, x1, y1, z1);
    this.pos.push(pos);
    this.nor.push(nor);
    this.idx.push(I);
    this.ari.push(aristas);
    this.nV += vn;
    this.nI += I.length;
    this.nA += aristas.length / 3;
    return this.nV >= this.limiteVertices ? this.cerrar() : null;
  }

  /** Cierra el lote en curso (null si está vacío) y empieza otro. */
  cerrar(): Lote | null {
    if (this.nV === 0) return null;
    const unir = <T extends Float32Array | Uint32Array>(partes: T[], n: number, C: new (n: number) => T): T => {
      const out = new C(n);
      let o = 0;
      for (const p of partes) {
        out.set(p, o);
        o += p.length;
      }
      return out;
    };
    const aristas = new Float32Array(this.nA * 3);
    let o = 0;
    for (const a of this.ari) {
      aristas.set(a, o);
      o += a.length;
    }
    const lote: Lote = {
      pos: unir(this.pos, this.nV * 3, Float32Array),
      nor: unir(this.nor, this.nV * 3, Float32Array),
      idx: unir(this.idx, this.nI, Uint32Array),
      aristas,
      trozos: Int32Array.from(this.trozos),
      colores: Float32Array.from(this.colores),
      cajas: Float32Array.from(this.cajas),
    };
    this.pos = []; this.nor = []; this.idx = []; this.ari = [];
    this.trozos = []; this.colores = []; this.cajas = [];
    this.nV = 0; this.nI = 0; this.nA = 0;
    return lote;
  }
}

/**
 * Cómo se ve un trozo: 0 = oculto, 1 = sólido, 2 = translúcido, 3 = AMBOS (la pieza a medio construir del avance de
 * obra: lo construido sólido y lo que falta como fantasma; el material de cada malla recorta su mitad). Sin aristas:
 * el contorno de la pieza entera engañaría sobre lo que ya está.
 */
export type Visibilidad = 0 | 1 | 2 | 3;

/**
 * Los índices de lo que se ve, separados en sólido y translúcido (dos llamadas de dibujo por lote), y el
 * de las aristas (solo de lo sólido: el contorno de un fantasma se veía como alambre). Una pieza oculta
 * no está en ningún índice, así tampoco la toca el rayo del clic.
 */
export function indicesVisibles(
  lote: Pick<Lote, 'idx' | 'trozos'>,
  visibilidad: (trozo: number) => Visibilidad,
): { solido: Uint32Array; translucido: Uint32Array; aristas: Uint32Array } {
  const nT = lote.trozos.length / T_PASO;
  const vis = new Uint8Array(nT);
  let nS = 0, nTr = 0, nA = 0;
  for (let t = 0; t < nT; t++) {
    const v = visibilidad(t);
    vis[t] = v;
    const o = t * T_PASO;
    if (v === 1) {
      nS += lote.trozos[o + T_IN]!;
      nA += lote.trozos[o + T_AN]!;
    } else if (v === 2) nTr += lote.trozos[o + T_IN]!;
    else if (v === 3) {
      nS += lote.trozos[o + T_IN]!;
      nTr += lote.trozos[o + T_IN]!;
    }
  }
  const solido = new Uint32Array(nS);
  const translucido = new Uint32Array(nTr);
  const aristas = new Uint32Array(nA);
  let s = 0, r = 0, a = 0;
  for (let t = 0; t < nT; t++) {
    const o = t * T_PASO;
    const i0 = lote.trozos[o + T_I0]!, iN = lote.trozos[o + T_IN]!;
    if (vis[t] === 1) {
      solido.set(lote.idx.subarray(i0, i0 + iN), s);
      s += iN;
      const a0 = lote.trozos[o + T_A0]!, aN = lote.trozos[o + T_AN]!;
      for (let k = 0; k < aN; k++) aristas[a++] = a0 + k;
    } else if (vis[t] === 2) {
      translucido.set(lote.idx.subarray(i0, i0 + iN), r);
      r += iN;
    } else if (vis[t] === 3) {
      solido.set(lote.idx.subarray(i0, i0 + iN), s);
      s += iN;
      translucido.set(lote.idx.subarray(i0, i0 + iN), r);
      r += iN;
    }
  }
  return { solido, translucido, aristas };
}

/** El trozo dueño de un vértice (los trozos van en orden de vértice): búsqueda binaria. */
export function trozoDeVertice(trozos: Int32Array, vertice: number): number {
  let lo = 0, hi = trozos.length / T_PASO - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (trozos[mid * T_PASO + T_V0]! <= vertice) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}
